import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../../Utils/DateTimeUtils";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Create from "./Create";
import Edit from "./Edit";

const List = () => {
  const navigate = useNavigate();
  const toast = useRef(null);

  const [sprints, setSprints] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [currentSprint, setCurrentSprint] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [error, setError] = useState("");

  const statusOptions = [
    { label: "All Statuses", value: null },
    { label: "Planning", value: "Planning" },
    { label: "Active", value: "Active" },
    { label: "Completed", value: "Completed" },
  ];

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const [sprintsRes, projectsRes] = await Promise.all([Http.get("/sprint"), Http.get("/project")]);

      setSprints(sprintsRes.data.data || []);
      setProjects(projectsRes.data.data || []);
    } catch (error) {
      console.error("Fetch error:", error);
      setError("Failed to load sprints. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (sprint) => {
    setCurrentSprint(sprint);
    setEditVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await Http.delete(`/sprint/${id}`);

      fetchData();

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Sprint deleted successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Delete error:", error);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to delete sprint",
        life: 3000,
      });
    }
  };

  const confirmDelete = (sprint) => {
    if (window.confirm(`Are you sure you want to delete the sprint "${sprint.name}"?`)) {
      handleDelete(sprint.id);
    }
  };

  const getProjectName = (projectId) => {
    if (!projectId) return "No Project";
    const project = projects.find((p) => p.id === projectId);
    return project ? project.title || project.name : "Unknown Project";
  };

  // Helper function to safely display user information
  const displayUserInfo = (user) => {
    if (!user) return "N/A";

    // If we have a user object with the name property
    if (user && typeof user === "object") {
      return (
        <div>
          {user.name || `${user.first_name || ""} ${user.last_name || ""}`}
          {!user.name && !user.first_name && !user.last_name && `User ID: ${user.id || "Unknown"}`}
        </div>
      );
    }

    // If only userId is available
    return <div>User ID: {user}</div>;
  };

  // Get status severity for tag styling
  const getStatusSeverity = (status) => {
    switch (status) {
      case "Planning":
        return "info";
      case "Active":
        return "success";
      case "Completed":
        return "warning";
      default:
        return "secondary";
    }
  };

  // Filter sprints based on search term and status
  const filteredSprints = sprints.filter(
    (sprint) =>
      (sprint.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sprint.goal && sprint.goal.toLowerCase().includes(searchTerm.toLowerCase()))) &&
      (!statusFilter || sprint.status === statusFilter)
  );

  const handleStartSprint = async (sprint) => {
    if (sprint.status !== "Planning") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Only sprints in Planning status can be started",
        life: 3000,
      });
      return;
    }

    try {
      await Http.post(`/sprint/${sprint.id}/start`, {});

      fetchData();

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Sprint started successfully",
        life: 3000,
      });
    } catch (error) {
      console.error("Error starting sprint:", error);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to start sprint",
        life: 3000,
      });
    }
  };

  const handleCompleteSprint = async (sprint) => {
    if (sprint.status !== "Active") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Only active sprints can be completed",
        life: 3000,
      });
      return;
    }

    if (window.confirm(`Are you sure you want to complete the sprint "${sprint.name}"? This will move all unfinished tasks back to the backlog.`)) {
      try {
        await Http.post(`/sprint/${sprint.id}/complete`, {});

        fetchData();

        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Sprint completed successfully",
          life: 3000,
        });
      } catch (error) {
        console.error("Error completing sprint:", error);

        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to complete sprint",
          life: 3000,
        });
      }
    }
  };

  const handlePlanSprint = (sprint) => {
    navigate("/sprint-planning");
  };

  return (
    <div>
      <IsLogin />
      <Toast ref={toast} />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Sprints</h5>
          <div className="d-flex justify-content-between align-items-center w-100 mt-2">
            <div className="d-flex align-items-center">
              <span className="p-input-icon-left mr-3" style={{ width: "300px" }}>
                <i className="pi pi-search" style={{ marginLeft: "4px" }} />
                <InputText
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search sprints..."
                  className="w-100"
                  style={{ paddingLeft: "20px" }}
                />
              </span>
              <Dropdown
                value={statusFilter}
                options={statusOptions}
                onChange={(e) => setStatusFilter(e.value)}
                placeholder="Filter by status"
                className="mr-3"
              />
            </div>
            <div>
              <Button label="Create Sprint" icon="pi pi-plus" className="p-button-success" onClick={() => setVisible(true)} />
            </div>
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-center p-5">
                <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
                <p className="mt-2">Loading sprints...</p>
              </div>
            ) : filteredSprints.length === 0 ? (
              <div className="text-center p-5">
                <i className="pi pi-folder-open" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                <p className="mt-2">No sprints found. Create your first sprint!</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mg-b-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Project</th>
                      <th>Goal</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Created By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSprints.map((sprint) => (
                      <tr key={sprint.id}>
                        <td>{sprint.name}</td>
                        <td>{getProjectName(sprint.project_id)}</td>
                        <td>
                          <div style={{ maxWidth: "200px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {sprint.goal || "No goal defined"}
                          </div>
                        </td>
                        <td>
                          <Tag value={sprint.status} severity={getStatusSeverity(sprint.status)} />
                        </td>
                        <td>{formatDate(sprint.start_date)}</td>
                        <td>{formatDate(sprint.end_date)}</td>
                        <td>
                          {displayUserInfo(sprint.createdBy)}
                          <small className="text-muted d-block">{formatDate(sprint.created_at)}</small>
                        </td>
                        <td>
                          <div className="d-flex">
                            {sprint.status === "Planning" && (
                              <>
                                <Button
                                  icon="pi pi-play"
                                  className="p-button-success p-button-sm mr-1"
                                  onClick={() => handleStartSprint(sprint)}
                                  tooltip="Start Sprint"
                                  tooltipOptions={{ position: "top" }}
                                />
                                <Button
                                  icon="pi pi-pencil"
                                  className="p-button-warning p-button-sm mr-1"
                                  onClick={() => handleEdit(sprint)}
                                  tooltip="Edit"
                                  tooltipOptions={{ position: "top" }}
                                />
                                <Button
                                  icon="pi pi-sitemap"
                                  className="p-button-info p-button-sm mr-1"
                                  onClick={() => handlePlanSprint(sprint)}
                                  tooltip="Plan Sprint"
                                  tooltipOptions={{ position: "top" }}
                                />
                              </>
                            )}
                            {sprint.status === "Active" && (
                              <Button
                                icon="pi pi-check"
                                className="p-button-success p-button-sm mr-1"
                                onClick={() => handleCompleteSprint(sprint)}
                                tooltip="Complete Sprint"
                                tooltipOptions={{ position: "top" }}
                              />
                            )}
                            {sprint.status === "Completed" && (
                              <Button
                                icon="pi pi-history"
                                className="p-button-info p-button-sm mr-1"
                                onClick={() => navigate(`/sprint-history/${sprint.id}`)}
                                tooltip="View History"
                                tooltipOptions={{ position: "top" }}
                              />
                            )}
                            <Button
                              icon="pi pi-trash"
                              className="p-button-danger p-button-sm"
                              onClick={() => confirmDelete(sprint)}
                              tooltip="Delete"
                              tooltipOptions={{ position: "top" }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog header="Create Sprint" visible={visible} style={{ width: "50vw" }} onHide={() => setVisible(false)} dismissableMask>
        <Create
          setVisible={setVisible}
          fetchData={fetchData}
          projects={projects}
          onSuccess={() => {
            toast.current.show({
              severity: "success",
              summary: "Success",
              detail: "Sprint created successfully",
              life: 3000,
            });
          }}
        />
      </Dialog>

      {/* Edit Dialog */}
      <Dialog header="Edit Sprint" visible={editVisible} style={{ width: "50vw" }} onHide={() => setEditVisible(false)} dismissableMask>
        <Edit setVisible={setEditVisible} fetchData={fetchData} rowData={currentSprint} projects={projects} />
      </Dialog>
    </div>
  );
};

export default List;
